import {
  ClickableElements,
  PageMetadataResponse,
} from "../types/page-extraction.types";
import getXPath from "get-xpath";

interface SimpleExtractionOptions {
  includeInvisible?: boolean;
  includeDisabled?: boolean;
  viewportOnly?: boolean;
  minSize?: { width: number; height: number };
}

class SimplePageExtraction {
  private options: SimpleExtractionOptions;

  constructor(options: SimpleExtractionOptions = {}) {
    this.options = {
      includeInvisible: false,
      includeDisabled: false,
      viewportOnly: false,
      minSize: { width: 5, height: 5 },
      ...options,
    };
  }

  extractCurrentPageMetadata(): PageMetadataResponse {
    const elements: ClickableElements[] = [];
    const allElements = document.querySelectorAll("*");

    for (let i = 0; i < allElements.length; i++) {
      const element = allElements[i];
      if (element && this.isClickable(element)) {
        const clickableElement = this.convertToClickableElements(element);
        if (clickableElement) {
          elements.push(clickableElement);
        }
      }
    }

    const _pageMetadata = this.getPageMetadata();

    return {
      elements,
      currentPage: {
        ..._pageMetadata,
        url: window.location.href,
      },
    };
  }

  private getPageMetadata() {
    const title =
      document?.title ||
      document.querySelector("title")?.textContent ||
      document
        .querySelector("meta[property='og:title']")
        ?.getAttribute("content") ||
      null;

    const description =
      document
        .querySelector("meta[name='description']")
        ?.getAttribute("content") ||
      document
        .querySelector("meta[property='og:description']")
        ?.getAttribute("content") ||
      null;

    return {
      title,
      description,
    };
  }

  private convertToClickableElements(
    element: Element
  ): ClickableElements | null {
    try {
      // Get visible text
      const text = this.getVisibleText(element);

      // Get position
      const position = this.getElementPosition(element);

      // Generate selectors
      const selectors = this.generateSelectorsArray(element);

      // Get attributes
      const attributes = this.getSimplifiedAttributes(element);

      // Determine type
      const type = this.determineElementType(element);

      if (type !== "other") {
        return {
          type,
          text,
          value: this.getValue(element),
          position,
          selectors,
          attributes,
        };
      }
      return null;
    } catch (error) {
      console.error("Error converting element to ClickableElements:", error);
      return null;
    }
  }

  private getValue(element: Element): string {
    if (element instanceof HTMLInputElement) {
      return element.value;
    }
    if (element instanceof HTMLSelectElement) {
      return element.value;
    }
    if (element instanceof HTMLTextAreaElement) {
      return element.value;
    }
    if (element instanceof HTMLButtonElement) {
      return element.value;
    }
    if (element instanceof HTMLAnchorElement) {
      return element.href;
    }
    return element.getAttribute("value") || "";
  }

  private getVisibleText(element: Element): string {
    // Priority order for text content
    const placeholder = (element as HTMLInputElement).placeholder;
    if (placeholder) {
      return placeholder;
    }

    const innerText = (element as HTMLElement).innerText?.trim();
    if (innerText) {
      return innerText;
    }

    const textContent = element.textContent?.trim();
    if (textContent) {
      return textContent;
    }

    const title = element.getAttribute("title");
    if (title) {
      return title;
    }

    const alt = (element as HTMLImageElement).alt;
    if (alt) {
      return alt;
    }

    // Fallback to tag name
    return element.tagName.toLowerCase();
  }

  private getElementPosition(element: Element) {
    const rect = element.getBoundingClientRect();
    return {
      x: Math.round(rect.x),
      y: Math.round(rect.y),
      width: Math.round(rect.width),
      height: Math.round(rect.height),
    };
  }

  private generateSelectorsArray(
    element: Element
  ): ClickableElements["selectors"] {
    const selectors: Array<{
      type: "id" | "class" | "xpath" | "tag";
      value: string;
    }> = [];

    // ID selector
    if (element.id) {
      selectors.push({ type: "id", value: element.id });
    }

    // Class selector
    if (element.getAttribute("class")) {
      selectors.push({
        type: "class",
        value: element.getAttribute("class") || "",
      });
    }

    // Tag selector
    selectors.push({ type: "tag", value: element.tagName.toLowerCase()! });

    // XPath selector
    try {
      const xpath = getXPath(element);
      if (xpath) {
        selectors.push({ type: "xpath", value: xpath });
      }
    } catch (error) {
      // XPath generation failed, skip it
    }

    return selectors;
  }

  private getSimplifiedAttributes(
    element: Element
  ): ClickableElements["attributes"] {
    const attributes: ClickableElements["attributes"] = {};

    // Basic attributes
    const value = (element as HTMLInputElement).value;
    if (value !== undefined && value !== null) {
      attributes.value = value;
    }

    const placeholder = (element as HTMLInputElement).placeholder;
    if (placeholder) {
      attributes.placeholder = placeholder;
    }

    const title = element.getAttribute("title");
    if (title) {
      attributes.title = title;
    }

    // Boolean attributes
    attributes.disabled = (element as HTMLInputElement).disabled || false;
    attributes.required = (element as HTMLInputElement).required || false;

    // Type-specific attributes
    if (element instanceof HTMLInputElement) {
      attributes.type = element.type;
    }

    if (element instanceof HTMLAnchorElement) {
      attributes.href = element.href;
    }

    return attributes;
  }

  private determineElementType(element: Element): ClickableElements["type"] {
    const tagName = element.tagName.toLowerCase();

    switch (tagName) {
      case "button":
        return "button";
      case "a":
        return "link";
      case "input":
        return "input";
      case "select":
        return "select";
      case "textarea":
        return "textarea";
      case "div":
        return "div";
      case "span":
        return "span";
      default:
        return "other";
    }
  }

  private isClickable(element: Element): boolean {
    if (element.nodeType !== Node.ELEMENT_NODE) return false;
    if (!this.options.includeInvisible && !this.isVisible(element))
      return false;
    if (!this.options.includeDisabled && this.isDisabled(element)) return false;
    if (this.options.viewportOnly && !this.isInViewport(element)) return false;

    const rect = element.getBoundingClientRect();
    if (
      rect.width < this.options.minSize!.width ||
      rect.height < this.options.minSize!.height
    )
      return false;

    return this.hasClickableProperties(element);
  }

  private isVisible(element: Element): boolean {
    try {
      const style = window.getComputedStyle(element);
      return (
        style.display !== "none" &&
        style.visibility !== "hidden" &&
        parseFloat(style.opacity) > 0 &&
        (element as HTMLElement).offsetWidth > 0 &&
        (element as HTMLElement).offsetHeight > 0
      );
    } catch (error) {
      return false;
    }
  }

  private isDisabled(element: Element): boolean {
    return (
      (element as HTMLInputElement).disabled ||
      element.getAttribute("aria-disabled") === "true"
    );
  }

  private isInViewport(element: Element): boolean {
    try {
      const rect = element.getBoundingClientRect();
      const windowHeight =
        window.innerHeight || document.documentElement.clientHeight;
      const windowWidth =
        window.innerWidth || document.documentElement.clientWidth;

      return (
        rect.top >= 0 &&
        rect.left >= 0 &&
        rect.bottom <= windowHeight &&
        rect.right <= windowWidth
      );
    } catch (error) {
      return false;
    }
  }

  private hasClickableProperties(element: Element): boolean {
    const tagName = element.tagName.toLowerCase();

    // First, check for native clickable elements
    if (
      ["button", "a", "input", "select", "textarea"].indexOf(tagName) !== -1
    ) {
      return true;
    }

    // Check for actual click handlers (not just cursor pointer)
    // const hasClickHandler =
    //   (element as any).onclick !== null ||
    //   (element as any).onmousedown !== null ||
    //   (element as any).onmouseup !== null ||
    //   (element as any).ontouchstart !== null ||
    //   (element as any).ontouchend !== null;

    // if (hasClickHandler) {
    //   return true;
    // }

    // Check for interactive ARIA roles
    const role = element.getAttribute("role");
    if (
      role === "button" ||
      role === "link" ||
      role === "tab" ||
      role === "menuitem"
    ) {
      return true;
    }

    // Check for positive tabindex (makes element focusable and clickable)
    const tabIndex = element.getAttribute("tabindex");
    if (tabIndex && tabIndex !== "-1" && parseInt(tabIndex) >= 0) {
      return true;
    }

    // // Check for data attributes that indicate interactivity
    // const hasDataAttributes =
    //   element.getAttribute("data-clickable") === "true" ||
    //   element.getAttribute("data-interactive") === "true" ||
    //   element.getAttribute("data-action") !== null;

    // if (hasDataAttributes) {
    //   return true;
    // }

    // Check for specific interactive attributes
    const hasInteractiveAttributes =
      element.getAttribute("onclick") !== null ||
      element.getAttribute("onmousedown") !== null ||
      element.getAttribute("onmouseup") !== null ||
      element.getAttribute("ontouchstart") !== null ||
      element.getAttribute("ontouchend") !== null;

    if (hasInteractiveAttributes) {
      return true;
    }

    // For non-native elements (div, span, etc.), require stronger evidence of clickability
    if (
      [
        "div",
        "span",
        "p",
        "section",
        "article",
        "header",
        "footer",
        "nav",
        "main",
        "aside",
      ].indexOf(tagName) !== -1
    ) {
      // These elements need explicit evidence of being clickable
      const style = window.getComputedStyle(element);
      const hasPointerCursor = style.cursor === "pointer";
      const hasUserSelect = style.userSelect !== "none";

      // Only consider clickable if it has BOTH pointer cursor AND some other evidence
      if (
        hasPointerCursor
        // &&
        // (hasClickHandler ||
        //   role ||
        //   tabIndex ||
        //   hasDataAttributes ||
        //   hasInteractiveAttributes)
      ) {
        return true;
      }

      // Don't include generic div/span elements just because they have pointer cursor
      return false;
    }

    return false;
  }
}

export default SimplePageExtraction;
